import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Image, FileText, Zap, ChevronDown, RefreshCw, ArrowRightLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  const navigation = [
    {
      name: 'Image Tools',
      icon: Image,
      items: [
        { name: 'Convert Format', href: '/image/convert', description: 'Convert between JPG, PNG, WebP, GIF' },
        { name: 'Resize Images', href: '/image/resize', description: 'Resize by pixels or percentage' },
        { name: 'Compress Images', href: '/image/compress', description: 'Reduce file size with quality control' },
      ]
    },
    {
      name: 'PDF Tools',
      icon: FileText,
      items: [
        { name: 'Merge PDFs', href: '/pdf/merge', description: 'Combine multiple PDFs into one' },
        { name: 'Split PDFs', href: '/pdf/split', description: 'Extract pages from PDFs' },
        { name: 'Compress PDFs', href: '/pdf/compress', description: 'Reduce PDF file size' },
      ]
    },
    {
      name: 'Convert to PDF',
      icon: RefreshCw,
      items: [
        { name: 'JPG to PDF', href: '/convert/jpg-to-pdf', description: 'Convert JPG images to PDF documents' },
        { name: 'Word to PDF', href: '/convert/word-to-pdf', description: 'Convert DOC/DOCX to PDF' },
        { name: 'PowerPoint to PDF', href: '/convert/powerpoint-to-pdf', description: 'Convert PPT/PPTX to PDF' },
        { name: 'Excel to PDF', href: '/convert/excel-to-pdf', description: 'Convert XLS/XLSX to PDF' },
        { name: 'HTML to PDF', href: '/convert/html-to-pdf', description: 'Convert web pages and HTML to PDF' },
      ]
    },
    {
      name: 'Convert from PDF',
      icon: ArrowRightLeft,
      items: [
        { name: 'PDF to JPG', href: '/convert/pdf-to-jpg', description: 'Extract images from PDF pages' },
        { name: 'PDF to Word', href: '/convert/pdf-to-word', description: 'Convert PDF to editable Word documents' },
        { name: 'PDF to PowerPoint', href: '/convert/pdf-to-powerpoint', description: 'Convert PDF to PowerPoint slides' },
        { name: 'PDF to Excel', href: '/convert/pdf-to-excel', description: 'Extract tables to Excel spreadsheets' },
        { name: 'PDF to PDF/A', href: '/convert/pdf-to-pdfa', description: 'Convert to archival PDF/A format' },
      ]
    }
  ]

  const isActiveRoute = (href: string) => location.pathname === href

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="bg-gradient-purple p-2 rounded-lg">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gradient">SnapTools</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((section) => (
              <div key={section.name} className="relative group">
                <button className="flex items-center space-x-1 text-gray-700 hover:text-[#8438ff] transition-colors">
                  <section.icon className="h-4 w-4" />
                  <span className="font-medium">{section.name}</span>
                  <ChevronDown className="h-3 w-3 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="p-4">
                    {section.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`block p-3 rounded-lg transition-colors ${
                          isActiveRoute(item.href)
                            ? 'bg-[#8438ff]/10 text-[#8438ff]'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium text-[#8438ff]">{item.name}</div>
                        <div className="text-sm text-gray-500 mt-1">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            
            <Link 
              to="/about"
              className={`text-gray-700 hover:text-[#8438ff] transition-colors font-medium ${
                isActiveRoute('/about') ? 'text-[#8438ff]' : ''
              }`}
            >
              About
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-4 space-y-4">
              {navigation.map((section) => (
                <div key={section.name}>
                  <div className="flex items-center space-x-2 text-gray-900 font-medium mb-2">
                    <section.icon className="h-4 w-4" />
                    <span>{section.name}</span>
                  </div>
                  <div className="ml-6 space-y-2">
                    {section.items.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block p-2 rounded-lg transition-colors ${
                          isActiveRoute(item.href)
                            ? 'bg-[#8438ff]/10 text-[#8438ff]'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium text-[#8438ff]">{item.name}</div>
                        <div className="text-sm text-gray-500">{item.description}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              
              <Link
                to="/about"
                onClick={() => setIsMenuOpen(false)}
                className={`block p-2 rounded-lg transition-colors font-medium ${
                  isActiveRoute('/about')
                    ? 'bg-[#8438ff]/10 text-[#8438ff]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                About
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header
